import { moveItemInArray } from "@angular/cdk/drag-drop";
import { SelectionModel } from "@angular/cdk/collections";
import { DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material";
import {
  IFilterResponse,
  SearchFilter,
} from "src/app/infrastructure/iFilter-component";
import { SearchMedicalReportAuditResponse } from "src/app/model/study-report/search-medical-report-audit-response";
import { SearchMedicalReportCategory } from "src/app/model/study-report/search-medical-report-category-response";
import { StudyReportSearchItem } from "src/app/model/study-report-search-item";
import { MenuService } from "src/app/services/menu.service";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { StudyReportService } from "src/app/services/study-report/study-report.service";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { Router } from "@angular/router";
import { StudyReportWizardService } from "src/app/services/wizard/study-report/study-report.wizard.service";
import { StudyReportWizardFactory } from "src/app/factory/study-report-wizard.factory";
import { StudyReportAuditDiagnosesDialogComponent } from "../../dialogs/study-report-audit-diagnoses-dialog/study-report-audit-diagnoses-dialog.component";

interface ColumnDefinition {
  field: string;
  title: string;
  visible: boolean;
  order: number;
  width: number;
}

@Component({
  selector: "app-study-report-audit-search",
  templateUrl: "./study-report-audit-search.component.html",
  styleUrls: ["./study-report-audit-search.component.css"],
  providers: [DatePipe],
})
export class StudyReportAuditSearchComponent implements OnInit, OnDestroy {
  loading = false;
  loadingAction = false;
  searchFilters: SearchFilter[] = [];
  dataSource: SearchMedicalReportAuditResponse[] = [];
  allItems: SearchMedicalReportAuditResponse[] = [];
  selection = new SelectionModel<SearchMedicalReportAuditResponse>(true, []);
  private lastSelectionAnchorIndex: number | null = null;

  @ViewChild("searchFilter", { static: false })
  searchFilter!: SearchFilterComponent;
  @ViewChild("pager", { static: true }) pager!: PagerControlComponent;

  columnFilters: { [field: string]: string } = {};

  private sessionColumnsKey = "study-report-audit-search.columns.v1";
  private sessionSortKey = "study-report-audit-search.sort.v1";
  private sessionColumnWidthsKey = "study-report-audit-search.column-widths.v1";

  private sortField: string | null = null;
  private sortDirection: "asc" | "desc" = "asc";
  private dragSourceField: string | null = null;
  private resizeListeners: { move: EventListener; up: EventListener } | null =
    null;

  columnsDefinition: ColumnDefinition[] = [
    { field: "gender", title: "Género", visible: true, order: 14, width: 90 },
    {
      field: "patient",
      title: "Paciente",
      visible: true,
      order: 5,
      width: 170,
    },
    {
      field: "publicationDate",
      title: "Fecha publicación",
      visible: true,
      order: 1,
      width: 130,
    },
    {
      field: "lastTransaction",
      title: "Última transacción",
      visible: true,
      order: 2,
      width: 150,
    },
    {
      field: "category",
      title: "Categoría",
      visible: true,
      order: 4,
      width: 120,
    },
    {
      field: "service",
      title: "Servicio",
      visible: true,
      order: 6,
      width: 120,
    },
    { field: "region", title: "Región", visible: true, order: 7, width: 120 },
    {
      field: "practice",
      title: "Práctica",
      visible: true,
      order: 8,
      width: 170,
    },
    {
      field: "protocol",
      title: "Protocolo",
      visible: true,
      order: 9,
      width: 100,
    },
    { field: "age", title: "Edad", visible: true, order: 11, width: 80 },
    {
      field: "transaction",
      title: "Transacción",
      visible: true,
      order: 12,
      width: 130,
    },
    {
      field: "memberNumber",
      title: "Nro. afiliado",
      visible: true,
      order: 13,
      width: 120,
    },
    {
      field: "birthDate",
      title: "Fecha nacimiento",
      visible: true,
      order: 15,
      width: 120,
    },
    {
      field: "specialty",
      title: "Especialidad",
      visible: true,
      order: 16,
      width: 130,
    },
    {
      field: "receptionTime",
      title: "Hora recepción",
      visible: true,
      order: 17,
      width: 120,
    },
    {
      field: "orderSector",
      title: "Sector orden",
      visible: true,
      order: 18,
      width: 120,
    },
    {
      field: "referringPhysician",
      title: "Médico derivante",
      visible: true,
      order: 19,
      width: 170,
    },
    {
      field: "referringPhysicianLicenseNumber",
      title: "Matrícula derivante",
      visible: true,
      order: 20,
      width: 150,
    },
  ];

  private readonly exportFieldMap: { [field: string]: string } = {
    publicationDate: "PublicationDate",
    lastTransaction: "LastTransaction",
    category: "Category",
    patient: "Patient",
    service: "Service",
    region: "Region",
    practice: "Practice",
    protocol: "Protocol",
    age: "Age",
    transaction: "Transaction",
    memberNumber: "MemberNumber",
    gender: "Gender",
    birthDate: "BirthDate",
    specialty: "Specialty",
    receptionTime: "ReceptionTime",
    orderSector: "OrderSector",
    referringPhysician: "ReferringPhysician",
    referringPhysicianLicenseNumber: "ReferringPhysicianLicenseNumber",
  };

  get availableColumns(): ColumnDefinition[] {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): ColumnDefinition[] {
    return this.columnsDefinition
      .filter((c) => c.visible)
      .sort((a, b) => a.order - b.order);
  }

  get exportColumns(): string[] {
    return this.orderedColumnsDefinition
      .map((col) => this.exportFieldMap[col.field])
      .filter((field) => !!field);
  }

  get exportColumnsName(): string[] {
    return this.orderedColumnsDefinition
      .filter((col) => !!this.exportFieldMap[col.field])
      .map((col) => col.title);
  }

  constructor(
    public dialog: MatDialog,
    private notificationToastService: NotificationToastService,
    private menuService: MenuService,
    private datePipe: DatePipe,
    private studyReportService: StudyReportService,
    private router: Router,
    private studyReportWizardService: StudyReportWizardService,
    private studyReportWizardFactory: StudyReportWizardFactory,
  ) {
    this.menuService.setMenuTitle("Informe", "Auditoría");
  }

  ngOnInit() {
    this.restoreColumns();
    this.restoreSort();
    this.restoreColumnWidths();
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  wizardServiceInitializeAndNavigate(
    item: SearchMedicalReportAuditResponse,
  ) {
    const studyReportSearchItem = this.createStudyReportSearchItem(item);
    this.studyReportWizardService.initialize(
      this.studyReportWizardFactory.CreateStudyReportWizard(
        studyReportSearchItem,
        true,
        false,
        true
      ),
    );
    this.router.navigate(["/study-report-wizard"]);
  }

  private createStudyReportSearchItem(
    item: SearchMedicalReportAuditResponse,
  ): StudyReportSearchItem {
    const studyReportSearchItem = new StudyReportSearchItem();
    studyReportSearchItem.MedicalApponitmentId = item.AppointmentId;
    studyReportSearchItem.StudyReportId = item.MedicalReportId
    studyReportSearchItem.Protocol = item.Protocol || "";
    studyReportSearchItem.PracticeName = item.Practice || "";
    studyReportSearchItem.Practice = item.Practice || "";
    studyReportSearchItem.ServiceName = item.Service || "";
    studyReportSearchItem.PatientLastName = item.Patient || "";
    studyReportSearchItem.PatientFirstName = "";
    studyReportSearchItem.PatientFullName = item.Patient || "";
    studyReportSearchItem.DateTime = item.PublicationDate;
    studyReportSearchItem.DeliveryDate = item.PublicationDate || new Date();
    studyReportSearchItem.MedicalOrderId = item.MedicalReportId;
    studyReportSearchItem.PacientId = item.PatientId;
    studyReportSearchItem.ServiceInformaTxControl = false;
    return studyReportSearchItem;
  }


  async setDiagnoses() {
    const selected = this.selection.selected;
    if (!selected || selected.length === 0) {
      this.notificationToastService.info(
        "Seleccione al menos un informe.",
        "Informe auditoría",
      );
      return;
    }

    const dialogRef = this.dialog.open(StudyReportAuditDiagnosesDialogComponent, {
      width: "40%",
      height: "220px",
    });

    const selectedCategory =
      (await dialogRef.afterClosed().toPromise()) as SearchMedicalReportCategory;

    if (!selectedCategory) {
      return;
    }

    const ids = selected
      .map((item) => item.AppointmentPracticeId)
      .filter((id) => id !== null && id !== undefined);

    if (ids.length === 0) {
      this.notificationToastService.info(
        "No se encontraron turnos válidos para clasificar.",
        "Informe auditoría",
      );
      return;
    }

    try {
      this.loadingAction = true;
      await this.studyReportService.setDiagnoses(selectedCategory.Id, ids);
      this.searchFilter.search();
    } catch (err) {
      const error = err as any;
      this.notificationToastService.error(
        JSON.stringify(error && error.error ? error.error : error),
        "Informe auditoría",
      );
    } finally {
      this.loadingAction = false;
    }
  }

  async exportDiagnoses() {
    try {
      this.loadingAction = true;
      const selected = this.selection.selected;
      if (!selected || selected.length === 0) {
        this.notificationToastService.info(
          "Seleccione al menos un informe.",
          "Informe auditoría",
        );
        return;
      }

      const ids = selected
        .map((item) => item.AppointmentPracticeId)
        .filter((id) => id !== null && id !== undefined);

      if (ids.length === 0) {
        this.notificationToastService.info(
          "No se encontraron turnos válidos para exportar.",
          "Informe auditoría",
        );
        return;
      }

      await this.studyReportService.exportDiagnoses(ids);
      this.searchFilter.search();
    } catch (err) {
      const error = err as any;
      this.notificationToastService.error(
        JSON.stringify(error && error.error ? error.error : error),
        "Informe auditoría",
      );
    } finally {
      this.loadingAction = false;
    }
  }

  get selectedAmount(): number {
    return this.selection && this.selection.selected
      ? this.selection.selected.length
      : 0;
  }

  onStartSearch() {
    this.loading = true;
  }

  onEndSearch(response: IFilterResponse<SearchMedicalReportAuditResponse>) {
    this.allItems = response.items || [];
    this.searchFilters = response.searchFilters;
    this.selection = new SelectionModel<SearchMedicalReportAuditResponse>(
      true,
      [],
    );
    this.lastSelectionAnchorIndex = null;
    this.refreshGrid();
  }

  selectedPageIndexChange(pagedItems: SearchMedicalReportAuditResponse[]) {
    this.dataSource = [...pagedItems];
    this.lastSelectionAnchorIndex = null;
    this.loading = false;
  }

  onRowCheckboxClick(
    event: MouseEvent,
    row: SearchMedicalReportAuditResponse,
  ) {
    event.stopPropagation();
    event.preventDefault();

    const rowIndex = this.dataSource.findIndex((item) => item === row);
    if (rowIndex < 0) {
      this.selection.toggle(row);
      return;
    }

    const useRangeSelection =
      event.shiftKey && this.lastSelectionAnchorIndex !== null;
    const keepExistingSelection = event.ctrlKey || event.metaKey;

    if (useRangeSelection) {
      const start = Math.min(this.lastSelectionAnchorIndex!, rowIndex);
      const end = Math.max(this.lastSelectionAnchorIndex!, rowIndex);

      if (!keepExistingSelection) {
        this.selection.clear();
      }

      this.dataSource
        .slice(start, end + 1)
        .forEach((item) => this.selection.select(item));
      this.lastSelectionAnchorIndex = rowIndex;
      return;
    }

    this.selection.toggle(row);
    this.lastSelectionAnchorIndex = rowIndex;
  }

  isAllSelected(): boolean {
    if (!this.selection.selected || !this.pager.getAllItems()) {
      return false;
    }

    const numSelected = this.selection.selected.length;
    const numRows = this.pager.getAllItems().length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.pager
          .getAllItems()
          .forEach((row: SearchMedicalReportAuditResponse) =>
            this.selection.select(row),
          );
  }

  getCellValue(item: SearchMedicalReportAuditResponse, field: string): string {
    switch (field) {
      case "medicalReportId":
        return item.MedicalReportId !== null &&
          item.MedicalReportId !== undefined
          ? item.MedicalReportId.toString()
          : "";
      case "publicationDate":
        return item.PublicationDate
          ? this.datePipe.transform(item.PublicationDate, "dd/MM/yyyy HH:mm") ||
              ""
          : "";
      case "lastTransaction":
        return item.LastTransaction || "";
      case "appointmentPracticeId":
        return item.AppointmentPracticeId !== null &&
          item.AppointmentPracticeId !== undefined
          ? item.AppointmentPracticeId.toString()
          : "";
      case "category":
        return item.Category || "";
      case "patient":
        return item.Patient || "";
      case "service":
        return item.Service || "";
      case "region":
        return item.Region || "";
      case "practice":
        return item.Practice || "";
      case "protocol":
        return item.Protocol || "";
      case "categoryId":
        return item.CategoryId !== null && item.CategoryId !== undefined
          ? item.CategoryId.toString()
          : "";
      case "age":
        return item.Age || "";
      case "transaction":
        return item.Transaction || "";
      case "memberNumber":
        return item.MemberNumber || "";
      case "gender":
        return item.Gender || "";
      case "birthDate":
        return item.BirthDate || "";
      case "specialty":
        return item.Specialty || "";
      case "receptionTime":
        return item.ReceptionTime || "";
      case "orderSector":
        return item.OrderSector || "";
      case "referringPhysician":
        return item.ReferringPhysician || "";
      case "referringPhysicianLicenseNumber":
        return item.ReferringPhysicianLicenseNumber || "";
      default:
        return "";
    }
  }

  // Column visibility
  isColumnVisible(field: string): boolean {
    const col = this.columnsDefinition.find((c) => c.field === field);
    return col ? col.visible : false;
  }

  setColumnVisibility(field: string, visible: boolean) {
    const col = this.columnsDefinition.find((c) => c.field === field);
    if (col) {
      col.visible = visible;
      this.saveColumns();
      this.refreshGrid();
    }
  }

  showAllColumns() {
    this.columnsDefinition.forEach((c) => (c.visible = true));
    this.saveColumns();
    this.refreshGrid();
  }

  private saveColumns() {
    const state = this.columnsDefinition.map((c) => ({
      field: c.field,
      visible: c.visible,
      order: c.order,
    }));
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(state));
  }

  private restoreColumns() {
    const raw = sessionStorage.getItem(this.sessionColumnsKey);
    if (!raw) {
      return;
    }
    try {
      const state: { field: string; visible: boolean; order: number }[] =
        JSON.parse(raw);
      state.forEach((s) => {
        const col = this.columnsDefinition.find((c) => c.field === s.field);
        if (col) {
          col.visible = s.visible;
          col.order = s.order;
        }
      });
    } catch {}
  }

  // Sort
  sortByColumn(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
    this.saveSort();
    this.refreshGrid();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return "unfold_more";
    }
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  private sortAllData(
    items: SearchMedicalReportAuditResponse[],
  ): SearchMedicalReportAuditResponse[] {
    const sortField = this.sortField;
    if (!sortField) {
      return items;
    }

    const dir = this.sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = this.getNormalizedColumnValue(a, sortField);
      const vb = this.getNormalizedColumnValue(b, sortField);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }

  private saveSort() {
    sessionStorage.setItem(
      this.sessionSortKey,
      JSON.stringify({ field: this.sortField, direction: this.sortDirection }),
    );
  }

  private restoreSort() {
    const raw = sessionStorage.getItem(this.sessionSortKey);
    if (!raw) {
      return;
    }
    try {
      const s = JSON.parse(raw);
      this.sortField = s.field;
      this.sortDirection = s.direction;
    } catch {}
  }

  // Column filters
  onColumnFilterInput(field: string, value: string) {
    this.columnFilters[field] = value;
    this.refreshGrid();
  }

  private applyColumnFilters(
    items: SearchMedicalReportAuditResponse[],
  ): SearchMedicalReportAuditResponse[] {
    return items.filter((item) => {
      return Object.keys(this.columnFilters).every((field) => {
        const filterVal = this.normalizeFilterValue(this.columnFilters[field]);
        if (!filterVal) {
          return true;
        }
        const cellVal = this.getNormalizedColumnValue(item, field);
        return cellVal.includes(filterVal);
      });
    });
  }

  private getNormalizedColumnValue(
    item: SearchMedicalReportAuditResponse,
    field: string,
  ): string {
    return this.normalizeFilterValue(this.getCellValue(item, field));
  }

  private normalizeFilterValue(value: string): string {
    if (!value) {
      return "";
    }
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  // Refresh
  private refreshGrid() {
    const filtered = this.applyColumnFilters(this.allItems);
    const sorted = this.sortAllData(filtered);
    this.pager.setItems(sorted);
  }

  // Column resize
  getColumnWidth(field: string): number {
    const col = this.columnsDefinition.find((c) => c.field === field);
    return col ? col.width : 150;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const col = this.columnsDefinition.find((c) => c.field === field);
    const startWidth = col ? col.width : 150;

    this.removeResizeListeners();

    const moveHandler: EventListener = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const newWidth = Math.max(50, startWidth + (mouseEvent.clientX - startX));
      if (col) {
        col.width = newWidth;
      }
    };

    const upHandler: EventListener = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);
    this.resizeListeners = { move: moveHandler, up: upHandler };
  }

  private removeResizeListeners() {
    if (this.resizeListeners) {
      document.removeEventListener("mousemove", this.resizeListeners.move);
      document.removeEventListener("mouseup", this.resizeListeners.up);
      this.resizeListeners = null;
    }
  }

  private saveColumnWidths() {
    const widths: { [field: string]: number } = {};
    this.columnsDefinition.forEach((c) => (widths[c.field] = c.width));
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(widths));
  }

  private restoreColumnWidths() {
    const raw = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!raw) {
      return;
    }
    try {
      const widths: { [field: string]: number } = JSON.parse(raw);
      this.columnsDefinition.forEach((c) => {
        if (widths[c.field] !== undefined) {
          c.width = widths[c.field];
        }
      });
    } catch {}
  }

  // Column drag/drop
  onHeaderDragStart(event: DragEvent, field: string) {
    this.dragSourceField = field;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (!this.dragSourceField || this.dragSourceField === targetField) {
      return;
    }

    const visible = this.orderedColumnsDefinition;
    const fromIdx = visible.findIndex((c) => c.field === this.dragSourceField);
    const toIdx = visible.findIndex((c) => c.field === targetField);

    if (fromIdx < 0 || toIdx < 0) {
      return;
    }

    moveItemInArray(visible, fromIdx, toIdx);
    visible.forEach((c, i) => {
      const col = this.columnsDefinition.find((x) => x.field === c.field);
      if (col) {
        col.order = i;
      }
    });

    this.saveColumns();
    this.dragSourceField = null;
  }

  onHeaderDragEnd() {
    this.dragSourceField = null;
  }
}
